import { useEffect, useRef } from 'react'
import LinearProductRow from './LinearProductRow'
import './LinearCatalog.css'

function clamp01(value) {
  if (value < 0) return 0
  if (value > 1) return 1
  return value
}

/* Scan line sits slightly below viewport center for a natural reading rhythm. */
const SCAN_LINE_RATIO = 0.62

/**
 * Linear catalog: a continuous vertical feed of product nodes along a glowing
 * spine. The spine's progress fill is scroll-driven (CSS var, no re-renders);
 * rows reveal on entry and their markers ignite as the scan line passes them.
 */
function LinearCatalog({ products, cartCount = 0, onAddToCart }) {
  const catalogRef = useRef(null)

  // Scroll-driven scan line + per-row "passed" state. Writes directly to the
  // DOM (CSS var + class toggles) so scrolling never triggers a re-render.
  useEffect(() => {
    const el = catalogRef.current
    if (!el) return undefined
    let rafId = 0

    const update = () => {
      rafId = 0
      const rect = el.getBoundingClientRect()
      if (rect.height <= 0) return
      const scanY = window.innerHeight * SCAN_LINE_RATIO
      const progress = clamp01((scanY - rect.top) / rect.height)
      el.style.setProperty('--linear-progress', progress.toFixed(4))

      const rows = el.querySelectorAll('.linear-row')
      for (let i = 0; i < rows.length; i += 1) {
        const rowRect = rows[i].getBoundingClientRect()
        const rowCenter = rowRect.top + rowRect.height / 2
        rows[i].classList.toggle('linear-row--passed', rowCenter <= scanY)
      }
    }

    const schedule = () => {
      if (rafId) return
      rafId = requestAnimationFrame(update)
    }

    update()
    window.addEventListener('scroll', schedule, { passive: true })
    window.addEventListener('resize', schedule)
    return () => {
      if (rafId) cancelAnimationFrame(rafId)
      window.removeEventListener('scroll', schedule)
      window.removeEventListener('resize', schedule)
    }
  }, [products.length])

  // Entrance reveal: rows fade/clip in once when they first enter the viewport.
  useEffect(() => {
    const el = catalogRef.current
    if (!el) return undefined
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return
          entry.target.classList.add('linear-row--in')
          observer.unobserve(entry.target)
        })
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.05 }
    )
    const rows = el.querySelectorAll('.linear-row:not(.linear-row--in)')
    rows.forEach((row) => observer.observe(row))
    return () => observer.disconnect()
  }, [products.length])

  return (
    <div className="linear-catalog" ref={catalogRef}>
      <div className="linear-spine" aria-hidden="true">
        <div className="linear-spine-track" />
        <div className="linear-spine-progress" />
        <div className="linear-spine-tip" />
      </div>
      {products.map((product, index) => (
        <LinearProductRow
          key={product.id}
          product={product}
          index={index}
          side={index % 2 === 1 ? 'right' : 'left'}
          cartCount={cartCount}
          onAddToCart={onAddToCart}
        />
      ))}
    </div>
  )
}

export default LinearCatalog
